import { ROOT_NODE, useEditor, type Node, type NodeId } from '@craftjs/core';
import { type ContainerProps } from '@builder4/shared/components/Container';
import { parseWidthToNumber, isRelativelyFillableElementV2 } from '@/utils/helperFunc';
import { useBreakpointAwareProp } from './useBreakpointAwareProp';
import { CONTENT_BLOCK_NAME } from '@/utils/constants';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { getBreakpointProp, setBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
export const useContainerHelper = () => {
  const { query, actions } = useEditor();
  const breakpoint = useAppSelector(selectBreakpoint);
  const { getBPProp, setBPProp } = useBreakpointAwareProp(CONTENT_BLOCK_NAME);

  const setInitialPlaceholderHeight = (placeholderHeight: number, newNodeId: string) => {
    if (placeholderHeight) {
      actions.history.ignore().setProp(newNodeId, (props: Partial<ContainerProps>) => {
        setBPProp(
          props,
          'placeholderHeight',
          placeholderHeight,
          query.node(newNodeId).get().data.displayName,
        );
      });
    }
  };

  const getTallestChild = (nodeId: string): Node | null => {
    const node = query.node(nodeId as string)?.get();
    if (!node) return null;
    const {
      data: { nodes: siblings },
    } = node;
    //find sibling with max height
    let maxHeight = 0;
    let child = null;
    siblings.forEach((sibling) => {
      const siblingNode = query.node(sibling as string)?.get();
      if (!siblingNode) return;
      const position = getBPProp(siblingNode.data.props, 'position');
      if (position === 'relative' || position === 'sticky') {
        const siblingHeight = siblingNode.dom?.clientHeight || 0;
        if (siblingHeight > maxHeight) {
          maxHeight = siblingHeight;
          child = siblingNode;
        }
      }
    });
    return child;
  };

  const calculateDistributedWidths = (
    parentId: string,
    newNodeId: string,
    bp: Breakpoint = breakpoint,
  ): {
    newNodeWidth: number;
    prevSiblingWidth: number;
    nextSiblingWidth: number;
    prevSiblingId: string | null;
    nextSiblingId: string | null;
  } | null => {
    const parentNode = query.node(parentId as string)?.get();
    if (!parentNode) return null;

    const siblings = getFillableChilds(parentNode.id as NodeId, bp) || [];

    const newNode = query.node(newNodeId)?.get();
    if (!newNode) return null;

    const index = siblings.findIndex((sibling) => sibling.id === newNodeId);
    if (index < 0) return null;

    const prevSibling = index > 0 ? siblings[index - 1] : null;
    const nextSibling = index < siblings.length - 1 ? siblings[index + 1] : null;

    const nextSiblingNode = nextSibling ? nextSibling : null;
    const prevSiblingNode = prevSibling ? prevSibling : null;
    let newNodeWidth = siblings.length === 1 ? 100 : 0;
    let prevSiblingWidth = 0;
    let nextSiblingWidth = 0;

    if (nextSiblingNode && prevSiblingNode) {
      const nextSiblingWidthStr = getBreakpointProp(
        nextSiblingNode.data.props as Partial<ContainerProps>,
        'width',
        bp,
      );
      const prevSiblingWidthStr = getBreakpointProp(
        prevSiblingNode.data.props as Partial<ContainerProps>,
        'width',
        bp,
      );
      const currentNextSiblingWidth = parseWidthToNumber(nextSiblingWidthStr || '0%');
      const currentPrevSiblingWidth = parseWidthToNumber(prevSiblingWidthStr || '0%');
      newNodeWidth = (currentPrevSiblingWidth + currentNextSiblingWidth) / 3;
      prevSiblingWidth = (currentPrevSiblingWidth * 2) / 3;
      nextSiblingWidth = (currentNextSiblingWidth * 2) / 3;
    } else {
      if (nextSiblingNode) {
        const nextSiblingWidthStr = getBreakpointProp(
          nextSiblingNode.data.props as Partial<ContainerProps>,
          'width',
          bp,
        );
        const currentNextSiblingWidth = parseWidthToNumber(nextSiblingWidthStr || '0%');
        newNodeWidth = currentNextSiblingWidth / 2;
        nextSiblingWidth = currentNextSiblingWidth / 2;
      } else if (prevSiblingNode) {
        const prevSiblingWidthStr = getBreakpointProp(
          prevSiblingNode.data.props as Partial<ContainerProps>,
          'width',
          bp,
        );
        const currentPrevSiblingWidth = parseWidthToNumber(prevSiblingWidthStr || '0%');
        newNodeWidth = currentPrevSiblingWidth / 2;
        prevSiblingWidth = currentPrevSiblingWidth / 2;
      }
    }

    return {
      newNodeWidth,
      prevSiblingWidth,
      nextSiblingWidth,
      prevSiblingId: prevSibling?.id as string | null,
      nextSiblingId: nextSibling?.id as string | null,
    };
  };

  const distributeSpaceOnNewObjectAdd = (
    parentId: string,
    newNodeId: string,
    bp: Breakpoint = breakpoint,
  ) => {
    //issue: calculating widths for current breakpoint but setting prop to base
    //solution: setting prop to current breakpoint
    const parentNode = query.node(parentId as string)?.get();
    if (!parentNode) return;
    const flexDirection = getBreakpointProp(
      parentNode.data.props as Partial<ContainerProps>,
      'flexDirection',
      bp,
    );
    if (flexDirection !== 'row') return;
    const widths = calculateDistributedWidths(parentId, newNodeId, bp);
    if (!widths) return;

    const { newNodeWidth, prevSiblingWidth, nextSiblingWidth, prevSiblingId, nextSiblingId } =
      widths;

    const methods = actions.history.merge();
    methods.setProp(newNodeId, (props: Partial<ContainerProps>) => {
      const newNode = query.node(newNodeId).get();
      setBreakpointProp(
        props,
        'width',
        `${newNodeWidth}%`,
        bp,
        newNode?.data.displayName || CONTENT_BLOCK_NAME,
      );
      setBreakpointProp(
        props,
        'flexBasis',
        `${newNodeWidth}%`,
        bp,
        newNode?.data.displayName || CONTENT_BLOCK_NAME,
      );
    });
    if (prevSiblingId) {
      methods.setProp(prevSiblingId, (props: Partial<ContainerProps>) => {
        const prevSibling = query.node(prevSiblingId).get();
        setBreakpointProp(
          props,
          'width',
          `${prevSiblingWidth}%`,
          bp,
          prevSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
        setBreakpointProp(
          props,
          'flexBasis',
          `${prevSiblingWidth}%`,
          bp,
          prevSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
      });
    }
    if (nextSiblingId) {
      methods.setProp(nextSiblingId, (props: Partial<ContainerProps>) => {
        const nextSibling = query.node(nextSiblingId).get();
        setBreakpointProp(
          props,
          'width',
          `${nextSiblingWidth}%`,
          bp,
          nextSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
        setBreakpointProp(
          props,
          'flexBasis',
          `${nextSiblingWidth}%`,
          bp,
          nextSibling?.data.displayName || CONTENT_BLOCK_NAME,
        );
      });
    }
  };

  const reAdustFillableObjectSpace = (
    parentId: string,
    newNodeId: string,
    bp: Breakpoint = breakpoint,
  ) => {
    const newNode = query.node(newNodeId).get();
    if (!newNode) return;

    const parentNode = query.node(parentId as string)?.get();
    if (!parentNode) return;

    if (parentId === ROOT_NODE || !isRelativelyFillableElementV2(newNode as Node, bp)) {
      return false;
    }

    const parentFlexDirection = getBreakpointProp(
      parentNode.data.props as Partial<ContainerProps>,
      'flexDirection',
      bp,
    );

    if (parentFlexDirection !== 'row') {
      return;
    }

    const equallyDividedSpace = hasEquallyDividedSpace(parentId as string, bp);
    if (equallyDividedSpace) {
      return equallyDivideParentSpace(parentId as string, bp);
    } else {
      distributeSpaceOnNewObjectAdd(parentId, newNodeId, bp);
    }
  };

  const equallyDivideParentSpace = (parentId: string | null, bp: Breakpoint = breakpoint) => {
    if (!parentId) return;

    const parentNode = query.node(parentId as string)?.get();

    if (!parentNode) return;
    const flexDirection = getBreakpointProp(
      parentNode.data.props as Partial<ContainerProps>,
      'flexDirection',
      bp,
    );
    if (flexDirection !== 'row') return;
    //issue: getting fillable childs for current breakpoint but setting prop to base
    //solution: setting prop to current breakpoint
    const childs = getFillableChilds(parentId, bp);
    const percent = 100 / childs.length;
    const methods = actions.history.merge();
    childs.forEach((child) => {
      methods.setProp(child.id, (props: Partial<ContainerProps>) => {
        setBreakpointProp(props, 'width', `${percent}%`, bp, child.data.displayName);
        setBreakpointProp(props, 'flexBasis', `${percent}%`, bp, child.data.displayName);
      });
    });
  };

  const hasEquallyDividedSpace = (parentId: string, bp: Breakpoint = breakpoint) => {
    const siblingsData = getFillableChilds(parentId, bp);
    if (siblingsData.length === 0) return false;
    if (siblingsData.length === 1) return true;

    const firstSiblingWidthStr = getBreakpointProp(
      siblingsData[0].data.props as Partial<ContainerProps>,
      'width',
      bp,
    );
    const refWidth = parseWidthToNumber(firstSiblingWidthStr || '0%');
    const sameWidth = siblingsData.every((sibling) => {
      const siblingWidthStr = getBreakpointProp(
        sibling.data.props as Partial<ContainerProps>,
        'width',
        bp,
      );
      return parseWidthToNumber(siblingWidthStr || '0%') === refWidth;
    });
    return sameWidth;
  };

  const getIndexInParent = (nodeId: string): number => {
    const node = query.node(nodeId as string)?.get();
    if (!node) return -1;
    const parentNode = query.node(node.data.parent as string)?.get();
    if (!parentNode) return -1;
    const nodes = parentNode.data.nodes;
    return nodes.indexOf(nodeId);
  };
  const getIndexInFillableChilds = (nodeId: string, bp: Breakpoint = breakpoint): number => {
    const node = query.node(nodeId as string)?.get();
    if (!node) return -1;
    const fillableChilds = getFillableChilds(node.data.parent as string, bp);
    return fillableChilds.indexOf(node);
  };

  const getFillableChilds = (nodeId: NodeId, bp: Breakpoint = breakpoint): Node[] => {
    const node = query.node(nodeId as string)?.get();
    if (!node) return [];
    const {
      data: { nodes: siblings },
    } = node;

    return siblings
      .map((siblingId: string) => {
        const sibling = query.node(siblingId as string)?.get();
        if (sibling && isRelativelyFillableElementV2(sibling, bp)) {
          return sibling;
        }
        return false;
      })
      .filter((sibling: Node | false) => sibling) as Node[];
  };
  const setFlexDirection = (nodeId: NodeId, flexDirection: 'row' | 'column') => {
    actions.setProp(nodeId, (props: Partial<ContainerProps>) => {
      setBPProp(props, 'flexDirection', flexDirection);
    });
    const actionMethods = actions.history.merge();
    const fillableChilds = getFillableChilds(nodeId);
    const childrenFlex = flexDirection === 'column' ? 'no' : 'yes';
    fillableChilds.forEach((node: Node) => {
      actionMethods.setProp(node.id, (props: Partial<ContainerProps>) => {
        setBPProp(props, 'fillSpace', childrenFlex, node.data.displayName);
        if (flexDirection === 'column') {
          setBPProp(props, 'width', '100%', node.data.displayName);
          //do not change flex basis as going back to direction row will keep the same width
        }
      });
    });
  };

  const getAllChildren = (parentId: NodeId): Node[] => {
    const childs = query.node(parentId).get().data.nodes;
    return childs.map((siblingId: NodeId) => query.node(siblingId).get()) as Node[];
  };

  const getNodeWidth = (node: Node, bp: Breakpoint = breakpoint): number => {
    const widthStr = getBreakpointProp(node.data.props as Partial<ContainerProps>, 'width', bp);
    return parseWidthToNumber(widthStr || '0%');
  };

  return {
    hasEquallyDividedSpace,
    setInitialPlaceholderHeight,
    equallyDivideParentSpace,
    getIndexInParent,
    reAdustFillableObjectSpace,
    distributeSpaceOnNewObjectAdd,
    calculateDistributedWidths,
    getTallestChild,
    setFlexDirection,
    getFillableChilds,
    getAllChildren,
    getNodeWidth,
    getIndexInFillableChilds,
  };
};
